import { Box, Button, Flex, Heading, IconButton, Link, Text } from '@chakra-ui/react'
import NumberFlow from '@number-flow/react'
import { LANGUAGES } from 'commons/constants/language'
import { toaster } from 'commons/design-system/components/Toaster'
import {
	BgColors,
	BorderRadius,
	Spacing,
	TextColors,
	TextColorsRaw
} from 'commons/design-system/tokens'
import type { SuccessCollectInfo } from 'commons/services/successService'
import { CopyIcon } from 'features/success-page/components/icons/CopyIcon'
import { ShareIcon } from 'features/success-page/components/icons/ShareIcon'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
	data: SuccessCollectInfo
	isMoneyPot?: boolean
}

const SuccessPageContent = ({ data, isMoneyPot = false }: Props) => {
	const { t, i18n } = useTranslation()
	const [amountCollectedToDisplay, setAmountCollectedToDisplay] = useState<number>(
		data.amount_collected_before_this_transaction
	)

	useEffect(() => {
		const timeout = setTimeout(() => {
			setAmountCollectedToDisplay(data.amount_collected)
		}, 1000)
		return () => clearTimeout(timeout)
	}, [data])

	const shareUrl = isMoneyPot
		? `${process.env.REACT_APP_POTS_URL}collect/pots?id=${data.slug}`
		: `${process.env.REACT_APP_PAYMENT_FORM_URL}l?t=${data.slug}`

	const handleCopyShareLink = async () => {
		try {
			await navigator.clipboard.writeText(shareUrl)
			toaster.create({
				title: t('success.share_link_copied'),
				type: 'success',
				duration: 2000,
				closable: true
			})
		} catch (error) {
			console.error('Error copying to clipboard:', error)
		}
	}

	return (
		<>
			<Box mt={4}>
				<Heading
					fontFamily='Degular'
					fontWeight={600}
					as='h1'
					fontSize='38px'
					color={TextColors.PRIMARY}
					mb={4}
				>
					{t('success.title')}
				</Heading>
				<Text
					fontSize='18px'
					fontFamily={'Montserrat'}
					fontWeight={500}
					color={TextColors.TERTIARY}
					lineHeight={'24px'}
				>
					{t('success.payment_amount', {
						amount: data.amount_paid.toString().replace('.', ',')
					})}
					<br />
					{t('success.payment_amount_receipt', {
						email: data.sender_email
					})}
				</Text>
			</Box>
			{isMoneyPot && data.show_total && (
				<Box
					w={'100%'}
					paddingY={8}
					mt={Spacing['3XL']}
					borderRadius={BorderRadius.XL}
					border='4px solid rgba(255, 255, 255, 0.75)'
					bg='rgba(255, 255, 255, 0.75)'
					backdropFilter='blur(9px)'
				>
					<Text fontSize='20px' fontFamily={'Degular'} fontWeight={500} color={TextColors.PRIMARY}>
						{t('success.total_collected')}
					</Text>
					<Box mt={-3}>
						<NumberFlow
							format={{ style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 }}
							trend={1}
							value={amountCollectedToDisplay}
							suffix={i18n.language === LANGUAGES.FRENCH ? ' €' : undefined}
							prefix={i18n.language !== LANGUAGES.FRENCH ? '€' : undefined}
						/>
					</Box>
				</Box>
			)}
			<Box
				w={'100%'}
				paddingY={8}
				paddingX={8}
				mt={Spacing['3XL']}
				borderRadius={BorderRadius.XL}
				border='4px solid rgba(255, 255, 255, 0.75)'
				bg='rgba(255, 255, 255, 0.75)'
				backdropFilter='blur(9px)'
				textAlign='center'
			>
				<Flex
					w='36px'
					h='36px'
					bg={BgColors.HIGHLIGHT_Z0}
					borderRadius={BorderRadius.L}
					align='center'
					justify='center'
					mx='auto'
					p={Spacing.XS}
				>
					<ShareIcon />
				</Flex>
				<Text
					fontFamily='Montserrat'
					fontSize='20px'
					fontWeight={700}
					color={TextColors.PRIMARY}
					mt={Spacing.S}
				>
					{isMoneyPot ? t('success.share_money_pot_title') : t('success.share_lydia_page_title')}
				</Text>
				<Text
					fontFamily='Montserrat'
					fontSize='14px'
					fontWeight={500}
					color={TextColors.TERTIARY}
					lineHeight='20px'
					mt={Spacing['3XS']}
					textAlign='center'
					whiteSpace='pre-line'
				>
					{isMoneyPot
						? t('success.share_money_pot_description')
						: t('success.share_lydia_page_description', { name: data.collector_name })}
				</Text>
				<Flex
					align='center'
					justify='space-between'
					gap={Spacing.M}
					mt={Spacing.ML}
					p={Spacing.M}
					bg={BgColors.Z5}
					borderRadius={BorderRadius.XL}
					boxShadow='0px 8px 40px 0px rgba(0, 0, 0, 0.04)'
				>
					<Text
						fontFamily='Montserrat'
						fontSize='16px'
						fontWeight={600}
						color={TextColors.SECONDARY}
						flex='1'
						truncate
						textAlign='left'
					>
						{shareUrl}
					</Text>
					<IconButton
						aria-label={t('success.share_link_copied')}
						onClick={handleCopyShareLink}
						size='sm'
						variant='ghost'
						color={TextColors.SECONDARY}
						_hover={{ bg: '#E2E8F0' }}
						minW='auto'
						h='auto'
						p='3px'
					>
						<CopyIcon />
					</IconButton>
				</Flex>
			</Box>
			<Flex
				align='center'
				justify='center'
				direction='column'
				w='100%'
				h='218px'
				borderRadius={BorderRadius.XL}
				overflow='hidden'
				position='relative'
				backgroundImage={`url(${process.env.REACT_APP_POTS_URL}collect/assets/img/collect/public/money-pot-banner.png)`}
				backgroundSize='cover'
				backgroundPosition='center'
				gap={Spacing.ML}
				mt={Spacing.ML}
			>
				<Box position='absolute' top={0} left={0} right={0} bottom={0} bg='rgba(0, 0, 0, 0.4)' />
				<Text
					fontSize='16px'
					fontFamily='Montserrat'
					fontWeight={700}
					color={TextColors.OPPOSITE_PRIMARY}
					textAlign='center'
					lineHeight={Spacing.M}
					zIndex={1}
					px={Spacing.L}
					whiteSpace='pre-line'
				>
					{data.collector_name} {t('success.organizer_uses_lydia_before')}{' '}
					<Link
						href={
							i18n.language === LANGUAGES.FRENCH
								? 'https://www.lydia.me/'
								: 'https://www.lydia.me/en'
						}
						target='_blank'
						rel='noopener noreferrer'
						textDecoration='underline'
						color={TextColors.OPPOSITE_PRIMARY}
					>
						Lydia
					</Link>{' '}
					{isMoneyPot
						? t('success.organizer_uses_lydia_after_moneypot')
						: t('success.organizer_uses_lydia_after_lydiapage')}
				</Text>
				<Button
					fontFamily='Montserrat'
					fontWeight={700}
					fontSize='15px'
					color={TextColorsRaw.PRIMARY}
					bg={BgColors.Z5}
					borderRadius='56px'
					h='42px'
					px={Spacing.SM}
					py={Spacing.S}
					zIndex={1}
					aria-label={isMoneyPot ? t('success.create_money_pot') : t('success.create_lydia_page')}
					onClick={() =>
						window.open(
							isMoneyPot
								? 'https://go.lydia.me/accountcreate?type=social_moneypot'
								: 'https://go.lydia.me/accountcreate?type=lydiapage',
							'_blank'
						)
					}
					_hover={{ bg: '#F5F5F5' }}
				>
					{isMoneyPot ? t('success.create_money_pot') : t('success.create_lydia_page')}
				</Button>
				{isMoneyPot && data.slug && (
					<Button
						variant='ghost'
						fontFamily='Montserrat'
						fontWeight={600}
						fontSize='14px'
						color={TextColors.OPPOSITE_PRIMARY}
						textDecoration='underline'
						zIndex={1}
						onClick={() => window.open(shareUrl, '_self')}
						_hover={{ bg: 'transparent', textDecoration: 'underline' }}
						_active={{ bg: 'transparent' }}
						h='auto'
						p={0}
					>
						{t('success.participate_again')}
					</Button>
				)}
			</Flex>
		</>
	)
}

export default SuccessPageContent
